import { NavLink } from 'react-router-dom';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import { useKeepInTouchAllScores, useKeepInTouchTemplatesQuery } from '../../../../../../queries/KeepInTouchAgent';
import { Button } from '../../../../../components-v2/Button';
import { ROUTES } from '../../../../../constants/routes';
import {
	QUERY_PARAM_APP_DRAWER_VALUE,
	QUERY_PARAM_DRAWER_CONTACT_ID,
} from '../../../../../containers/PeopleV2/PeopleContainer/hooks/useContactsPageAppDrawerQueryParamState';
import { PeopleIcon } from '../../../../svgs/icons/PeopleIcon';
import { AgentPanelFooter, AgentPanelItem, AgentPanelLink, AgentPanelMessage } from '../../AgentPanel';
import { agentTileState } from '../../AgentTile';
import { styleSheet } from '../../styles';
import { timeSinceContactLabel } from './helpers';

const TELEMETRY_CATEGORY = 'DashboardKeepInTouchAgent';
const PANEL_ITEM_LIMIT = 3;
const MIN_SCORE = 90;

const contactPath = (contactId: string) =>
	`${ROUTES.contacts.keepInTouch}?${new URLSearchParams({
		[QUERY_PARAM_APP_DRAWER_VALUE]: 'contact-info',
		[QUERY_PARAM_DRAWER_CONTACT_ID]: contactId,
	})}`;

export const useKeepInTouchAgent = () => {
	const userSession = useUserSession();
	const { logEvent } = useEventLogging(TELEMETRY_CATEGORY);
	const enabled = !!userSession.account?.features?.keepInTouch?.enabled;
	const templatesQuery = useKeepInTouchTemplatesQuery({
		enabled,
		meta: { errorLogging: { category: TELEMETRY_CATEGORY, action: 'DashboardKeepInTouchLoadTemplates-Error' } },
	});
	const hasTemplates = (templatesQuery.data?.length ?? 0) > 0;
	const scoresQuery = useKeepInTouchAllScores({
		pageSize: PANEL_ITEM_LIMIT,
		minScore: MIN_SCORE,
		enabled: hasTemplates,
		meta: { errorLogging: { category: TELEMETRY_CATEGORY, action: 'DashboardKeepInTouchLoadScores-Error' } },
	});

	if (!enabled || templatesQuery.isError || (!templatesQuery.isPending && !hasTemplates)) {
		return null;
	}

	const agent = {
		key: 'keepInTouch',
		name: 'Keep in Touch',
		icon: <PeopleIcon />,
		iconStyles: styleSheet.keepInTouchIcon,
	};
	if (!hasTemplates || scoresQuery.isPending) {
		return { ...agent, tile: null, panel: null };
	}
	if (scoresQuery.isError) {
		return {
			...agent,
			tile: agentTileState('Couldn’t load', { isError: true }),
			panel: (
				<AgentPanelMessage
					action={
						<Button size='sm' variant='secondary' onClick={() => scoresQuery.refetch()}>
							Try again
						</Button>
					}
				>
					We couldn’t load your suggestions.
				</AgentPanelMessage>
			),
		};
	}

	const firstPage = scoresQuery.data.pages[0];
	const count = firstPage?.totalCount ?? 0;
	const openPage = (from: string) => logEvent('OpenKeepInTouchClicked', { from });

	if (count === 0) {
		return {
			...agent,
			tile: agentTileState('No one to reach out to'),
			panel: (
				<>
					<AgentPanelMessage>Nobody needs a check-in right now.</AgentPanelMessage>
					<AgentPanelFooter>
						<AgentPanelLink to={ROUTES.contacts.keepInTouch} onClick={() => openPage('empty')}>
							Open Keep in Touch
						</AgentPanelLink>
					</AgentPanelFooter>
				</>
			),
		};
	}

	return {
		...agent,
		tile: agentTileState('to reach out to', { count }),
		panel: (
			<>
				{(firstPage?.values ?? []).slice(0, PANEL_ITEM_LIMIT).map(score => (
					<AgentPanelItem
						key={score.id}
						title={score.contactName || 'Unnamed contact'}
						meta={
							[
								score.companyName,
								score.daysSinceLastContact > 0
									? `no contact in ${timeSinceContactLabel(score.daysSinceLastContact)}`
									: null,
							]
								.filter(Boolean)
								.join(' · ') || undefined
						}
						detail={score.reasoning ?? undefined}
						action={
							<Button variant='secondary' size='sm' asChild>
								<NavLink to={contactPath(score.contactId)} onClick={() => openPage('contact')}>
									Open
								</NavLink>
							</Button>
						}
					/>
				))}
				<AgentPanelFooter>
					<AgentPanelLink to={ROUTES.contacts.keepInTouch} onClick={() => openPage('seeAll')}>
						{`See all ${count}`}
					</AgentPanelLink>
				</AgentPanelFooter>
			</>
		),
	};
};
